import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, ArrowRight, Truck } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function DriverRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", password: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if ((form.password || "").length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters." });
      return;
    }
    setSubmitting(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        name: `${form.firstName} ${form.lastName}`.trim() || form.email,
        phone: form.phone,
        role: "driver"
      });
      toast({ title: "Driver account created", description: "You're signed in to the driver portal." });
      navigate("/driver-dashboard");
    } catch (err) {
      toast({ title: "Couldn't create account", description: err?.message?.replace("Firebase: ", "") || "Try a different email." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="driver"
      title="Create driver account"
      subtitle="Set up your driver login to run routes, capture proof of delivery and log stops."
      footer={
        <>
          Already have a driver login?{" "}
          <Link to="/driver-login" className="text-[#f59321] font-semibold hover:underline">Sign in</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="driver-register-form">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#f59321]/10 border border-[#f59321]/30 text-sm text-[#0f2847]">
          <Truck className="w-4 h-4 text-[#f59321]" />
          Creating a 518 Courier driver profile.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" icon={User} value={form.firstName} onChange={(v) => update("firstName", v)} placeholder="Alex" required testid="dr-first" />
          <Field label="Last name" value={form.lastName} onChange={(v) => update("lastName", v)} placeholder="Rivera" required testid="dr-last" />
        </div>
        <Field label="Email" type="email" icon={Mail} value={form.email} onChange={(v) => update("email", v)} placeholder="driver@518courier.com" required testid="dr-email" />
        <Field label="Phone" type="tel" icon={Phone} value={form.phone} onChange={(v) => update("phone", v)} placeholder="(518) 954-4701" testid="dr-phone" />
        <Field label="Password" type="password" icon={Lock} value={form.password} onChange={(v) => update("password", v)} placeholder="At least 6 characters" required testid="dr-password" />
        <Button type="submit" disabled={submitting} data-testid="driver-register-submit" className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60">
          {submitting ? "Creating…" : "Create driver account"}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </form>
    </AuthLayout>
  );
}

function Field({ label, icon: Icon, type = "text", value, onChange, placeholder, required, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0f2847] font-semibold">{label}{required && " *"}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className={`h-12 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321] ${Icon ? "pl-10" : ""}`} />
      </div>
    </div>
  );
}
